import * as React from "react"
import { useLogInMagicLinkMutation } from "@digits-graphql/frontend/graphql-public"
import { RequestScheme, useMagicLinkToken } from "@digits-shared/hooks/useMagicLinkToken"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import { jwtDecode } from "jwt-decode"
import { useSignIn } from "src/frontend/components/Public/hooks/useSignIn"
import { getMagicLinkScheme } from "src/frontend/components/Public/LogIn/getMagicLinkScheme"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"
import type FrontendSession from "src/frontend/session"

interface magicLinkJWT {
  exp: number
  iat: number
  email: string
}

export function useMagicLinkLogIn() {
  const { dispatch } = useOnboardingContext()
  const { location } = useRouter()
  const { onSignInCompleted } = useSignIn()
  const { token, scheme } = useMagicLinkToken()
  const loggingInRef = React.useRef(false)

  const session = useSession<FrontendSession>()
  const activeLegalEntityId = session.lastKnownLegalEntityId

  const redirectPath = location.state?.from?.pathname

  const onMagicLinkError = React.useCallback(() => {
    if (!token) {
      dispatch({ type: "SHOW_SIGN_IN" })
      return
    }

    try {
      const jwt = jwtDecode<magicLinkJWT>(token)
      dispatch({ type: "SHOW_MAGIC_LINK_EXPIRED", emailAddress: jwt.email, redirectPath })
    } catch (e) {
      TrackJS?.track(e)
      dispatch({ type: "SHOW_SIGN_IN" })
    }
  }, [dispatch, redirectPath, token])

  // if the origin location has a magic link token, attempt to exchange it for a session
  const [logInMagicLink, { loading }] = useLogInMagicLinkMutation({
    onCompleted: onSignInCompleted,
    onError: onMagicLinkError,
    context: { publicAPI: true },
  })

  React.useEffect(() => {
    async function logIn() {
      if (token && !loading && !loggingInRef.current) {
        loggingInRef.current = true
        const { state } = await getMagicLinkScheme()
        logInMagicLink({
          variables: {
            token,
            state,
            activeLegalEntityId,
          },
        })
      }
    }

    if (scheme === RequestScheme.Digits) {
      const url = new URL(window.location.href.replace(window.location.protocol, "digits:"))
      url.pathname = redirectPath
      if (token) {
        url.searchParams.set("ml", token)
      }
      window.location.href = url.href
    } else {
      logIn()
    }
  }, [activeLegalEntityId, dispatch, loading, logInMagicLink, redirectPath, scheme, token])

  return React.useMemo(
    () => ({
      loading,
      token,
      scheme,
    }),
    [loading, scheme, token]
  )
}
